// A AMOSTRA DE CADA FORMATO, DESENHADA A PARTIR DA GRADE DE VERDADE — `T12.5`.
//
// ── O ACHADO, NA LETRA DE QUEM TESTOU (28/09/2026, aos 0:16) ────────────────────────────────
//
//     "Poderia ter um design melhor de explicação, fica muito fino, fica diferente. Até a tirinha
//      mais grossa; o símbolo poderia ser mais próximo da folha A4, só que um pouco menor."
//
// E a descrição do relato é o número: *"«Rolagem vertical» é um traço quase invisível, e A4, Grade
// livre e Capa usam o mesmo desenho."*
//
// MEDIDO EM 03/10, no navegador (`rastro/a-escolha-do-formato-abre-no-topo.mjs`):
//
//     amostras distintas ......... 4 de 6
//     a mais fina ................ 15 pt de largura
//
// Quatro de seis, e as três iguais são justamente as três folhas A4 — página de revista, grade
// livre e capa têm 1240 × 1754 e, numa amostra que só tem proporção, saem idênticas. A amostra
// respondia *"qual é o formato da folha?"*, e a pergunta de quem escolhe é outra: **"como vai ser
// a minha página?"**
//
// ── A RESPOSTA ESTAVA NO PRÓPRIO PRODUTO, E ELA NÃO É DESENHO MEU ───────────────────────────
//
// Cada formato já declara a grade de quadros com que a folha nasce — `PADROES_PAGINA[id].grade`,
// em fração da página. É o que `comicComGradeInicial` usa para montar a folha de verdade. Então a
// amostra passa a desenhar ESSA grade: o que a criança vê na miniatura é literalmente o que vai
// aparecer quando a folha abrir.
//
// Isso resolve as três iguais sem eu inventar nada: a página de revista mostra 6 quadros em 2
// colunas, a tirinha mostra 3 lado a lado, a rolagem vertical mostra 5 empilhados, a prancha
// didática mostra 4 quadros e a faixa de legenda embaixo.
//
// ── E AS DUAS QUE NÃO TÊM GRADE, QUE SÃO DUAS COISAS DIFERENTES ─────────────────────────────
//
// `grade-livre` e `capa` têm a grade VAZIA, as duas de propósito — e o texto de cada uma promete
// coisas opostas: *"sem grade pronta: arraste os quadros para onde quiser"* e *"sem quadros: a
// imagem grande com o título por cima"*. Uma amostra vazia para as duas repetiria o defeito.
//
// Então a grade livre recebe contorno TRACEJADO, que é o sinal universal de "aqui você posiciona",
// e a capa recebe uma área cheia com a faixa do título em cima — que é o que ela é.
//
// ── POR QUE SVG, E NÃO UM `<span>` COM BORDA ────────────────────────────────────────────────
//
// A amostra antiga era um `<span>` dimensionado por `style`. Desenhar seis retângulos dentro dele
// pediria seis filhos posicionados em porcentagem, e o traço grosso que ele pede não existe em
// borda de elemento sem comer o espaço interno. Em SVG o traço é `stroke-width`, cresce para fora
// e para dentro igualmente, e a grade é um `map` sobre o dado que já existe.

import { FormatoPagina, PADROES_PAGINA } from "../../api/documento.types";

/** A caixa da miniatura. Maior que os 56 px da amostra antiga: ela agora mostra conteúdo. */
const CAIXA = 64;

/** O traço, e ele é a queixa literal — *"fica muito fino"*. */
const TRACO_FOLHA = 2.6;
const TRACO_QUADRO = 1.8;

/**
 * A moldura da folha, encaixada na caixa preservando a proporção real.
 *
 * O piso de 14 px existe pelo webtoon: 800 × 4000 proporcional daria 13 px de largura, e a
 * medição de hoje mostrou a amostra antiga com **15 pt** — *"um traço quase invisível"*. Catorze
 * mais o traço grosso é o mínimo em que ainda se reconhece uma tira alta, e abaixo disso a
 * miniatura deixa de informar.
 */
function folha(largura: number, altura: number) {
  const escala = Math.min(CAIXA / largura, CAIXA / altura);
  return {
    w: Math.max(Math.round(largura * escala), 14),
    h: Math.max(Math.round(altura * escala), 14),
  };
}

export function AmostraDoFormato({ formato }: { formato: FormatoPagina }) {
  const padrao = PADROES_PAGINA[formato];
  const { w, h } = folha(padrao.largura, padrao.altura);
  const m = TRACO_FOLHA / 2;

  // A grade vem em fração da página (0 a 1), que é como `comicComGradeInicial` a consome.
  const quadros = padrao.grade.map((q, i) => (
    <rect
      key={i}
      x={q.x * w}
      y={q.y * h}
      width={q.w * w}
      height={q.h * h}
      fill="var(--hq-superficie, #fff)"
      stroke="currentColor"
      strokeWidth={TRACO_QUADRO}
      vectorEffect="non-scaling-stroke"
    />
  ));

  return (
    <svg
      className="formato-amostra-desenho"
      width={w + TRACO_FOLHA}
      height={h + TRACO_FOLHA}
      viewBox={`${-m} ${-m} ${w + TRACO_FOLHA} ${h + TRACO_FOLHA}`}
      aria-hidden="true"
      focusable="false"
    >
      {/* A folha. Tracejada só na grade livre, que é a que promete folha sem quadros prontos. */}
      <rect
        x={0}
        y={0}
        width={w}
        height={h}
        rx={2}
        fill="var(--hq-fundo-suave, #eaf6d9)"
        stroke="currentColor"
        strokeWidth={TRACO_FOLHA}
        strokeDasharray={formato === "grade-livre" ? "4 3" : undefined}
      />

      {quadros}

      {/* A CAPA não tem quadro: ela é uma arte só com o título por cima, e é isso que a amostra
          mostra — senão ela fica idêntica à grade livre, que é o defeito de origem. */}
      {formato === "capa" && (
        <>
          <rect
            x={w * 0.1}
            y={h * 0.26}
            width={w * 0.8}
            height={h * 0.62}
            fill="currentColor"
            opacity={0.22}
          />
          <rect x={w * 0.1} y={h * 0.1} width={w * 0.8} height={h * 0.1} fill="currentColor" />
        </>
      )}
    </svg>
  );
}

export default AmostraDoFormato;
